import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { 
  MapPin, Building2, Users, Heart, Target, TrendingUp, 
  BarChart2, ArrowRight, Info, Loader2, X, CheckCircle2
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helper
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  
  const response = await fetch(`${API_URL}/api${endpoint}`, { ...options, headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erro' }));
    throw error;
  }
  return response.json();
};

const COLORS = ['#0066CC', '#00AA66', '#F59E0B', '#EF4444'];

const METRIC_ICONS = {
  num_instituicoes: Building2,
  num_voluntarios: Heart,
  num_utentes: Users,
  num_missoes: Target,
  num_necessidades_pendentes: BarChart2,
  qtd_total_necessidades: BarChart2,
  qualidade_media: TrendingUp
};

export default function ComparadorDistritosPage() {
  const [distritos, setDistritos] = useState([]);
  const [selectedDistritos, setSelectedDistritos] = useState([]);
  const [comparacao, setComparacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    loadDistritos();
  }, []);

  const loadDistritos = async () => {
    try {
      const data = await apiCall('/comparador/distritos/lista');
      setDistritos(data || []);
    } catch (error) {
      console.error('Error loading districts:', error);
      toast.error('Erro ao carregar distritos');
    } finally {
      setLoading(false);
    }
  };

  const handleDistritoToggle = (distritoId) => {
    setSelectedDistritos(prev => {
      if (prev.includes(distritoId)) {
        return prev.filter(id => id !== distritoId);
      }
      if (prev.length >= 4) {
        toast.warning('Máximo de 4 distritos para comparar');
        return prev;
      }
      return [...prev, distritoId];
    });
  };

  const handleCompare = async () => {
    if (selectedDistritos.length < 2) {
      toast.error('Selecione pelo menos 2 distritos para comparar');
      return;
    }

    setComparing(true);
    try {
      const data = await apiCall(`/comparador/distritos?distritos=${selectedDistritos.join(',')}`);
      setComparacao(data);
    } catch (error) {
      console.error('Error comparing:', error);
      toast.error(error.detail || 'Erro ao comparar distritos');
    } finally {
      setComparing(false);
    }
  };

  const clearSelection = () => {
    setSelectedDistritos([]);
    setComparacao(null);
  };

  // Prepare chart data
  const getChartData = (metricKey) => {
    if (!comparacao) return [];
    return comparacao.distritos.map((d, i) => ({
      name: d.distrito_nome,
      value: d[metricKey] || 0,
      fill: COLORS[i % COLORS.length]
    }));
  };

  // Prepare radar data
  const getRadarData = () => {
    if (!comparacao) return [];
    
    // Normalize values to 0-100 scale for each metric
    const metrics = comparacao.metricas.filter(m => m.key !== 'qualidade_media');
    
    return metrics.map(metric => {
      const values = comparacao.distritos.map(d => d[metric.key] || 0);
      const max = Math.max(...values, 1);
      
      const dataPoint = { metric: metric.label };
      comparacao.distritos.forEach((d, i) => {
        dataPoint[d.distrito_nome] = Math.round((d[metric.key] / max) * 100);
      });
      
      return dataPoint;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Comparador de Distritos</h1>
        <p className="text-slate-500 mt-1">
          Compare estatísticas de instituições, necessidades e voluntários entre diferentes regiões
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          Selecione entre 2 e 4 distritos para visualizar uma comparação detalhada de métricas.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* District Selection */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0066CC]" />
              Selecionar Distritos
            </CardTitle>
            <CardDescription>
              Escolha até 4 distritos ({selectedDistritos.length}/4 selecionados)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {distritos.map((distrito) => (
                <div 
                  key={distrito.id} 
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedDistritos.includes(distrito.id) 
                      ? 'bg-blue-50 border-blue-300' 
                      : 'bg-white border-slate-200 hover:border-blue-200'
                  }`}
                  onClick={() => handleDistritoToggle(distrito.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedDistritos.includes(distrito.id)}
                      onCheckedChange={() => handleDistritoToggle(distrito.id)}
                    />
                    <div>
                      <Label className="font-medium cursor-pointer">{distrito.nome}</Label>
                      <p className="text-xs text-slate-500">{distrito.num_instituicoes || 0} instituições</p>
                    </div>
                  </div>
                  {selectedDistritos.includes(distrito.id) && (
                    <Badge className={`${COLORS[selectedDistritos.indexOf(distrito.id) % COLORS.length]} text-white`} style={{backgroundColor: COLORS[selectedDistritos.indexOf(distrito.id) % COLORS.length]}}>
                      {selectedDistritos.indexOf(distrito.id) + 1}
                    </Badge>
                  )}
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="flex gap-2">
              <Button 
                onClick={handleCompare} 
                disabled={selectedDistritos.length < 2 || comparing}
                className="flex-1"
                data-testid="compare-btn"
              >
                {comparing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A comparar...
                  </>
                ) : (
                  <>
                    <BarChart2 className="w-4 h-4 mr-2" />
                    Comparar
                  </>
                )}
              </Button>
              {selectedDistritos.length > 0 && (
                <Button variant="outline" onClick={clearSelection}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Comparison Results */}
        <div className="lg:col-span-2 space-y-6">
          {comparacao ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {comparacao.distritos.map((d, i) => (
                  <Card key={d.distrito_id} style={{ borderTopColor: COLORS[i], borderTopWidth: 3 }}>
                    <CardContent className="pt-4">
                      <h3 className="font-semibold text-slate-900">{d.distrito_nome}</h3>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Instituições:</span>
                          <span className="font-medium">{d.num_instituicoes}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Utentes:</span>
                          <span className="font-medium">{d.num_utentes?.toLocaleString('pt-PT')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Voluntários:</span>
                          <span className="font-medium">{d.num_voluntarios}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bar Chart - Institutions */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Instituições
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getChartData('num_instituicoes')} layout="vertical">
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {getChartData('num_instituicoes').map((entry, index) => (
                              <rect key={index} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Bar Chart - Utentes */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Utentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getChartData('num_utentes')} layout="vertical">
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(value) => value.toLocaleString('pt-PT')} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {getChartData('num_utentes').map((entry, index) => (
                              <rect key={index} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Necessidades Pendentes */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart2 className="w-4 h-4" />
                      Necessidades Pendentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getChartData('num_necessidades_pendentes')} layout="vertical">
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {getChartData('num_necessidades_pendentes').map((entry, index) => (
                              <rect key={index} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Radar Chart - Overview */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Visão Geral (Normalizado)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={getRadarData()}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="metric" tick={{ fontSize: 9 }} />
                          <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                          {comparacao.distritos.map((d, i) => (
                            <Radar
                              key={d.distrito_id}
                              name={d.distrito_nome}
                              dataKey={d.distrito_nome}
                              stroke={COLORS[i]}
                              fill={COLORS[i]}
                              fillOpacity={0.2}
                            />
                          ))}
                          <Legend wrapperStyle={{ fontSize: 10 }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Comparação Detalhada</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Métrica</th>
                          {comparacao.distritos.map((d, i) => (
                            <th key={d.distrito_id} className="text-right py-3 px-2 font-medium" style={{ color: COLORS[i] }}>
                              {d.distrito_nome}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {comparacao.metricas.map((metric) => (
                          <tr key={metric.key} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-2 text-slate-700">{metric.label}</td>
                            {comparacao.distritos.map((d) => (
                              <td key={d.distrito_id} className="text-right py-3 px-2 font-medium">
                                {metric.format === 'percent' 
                                  ? `${d[metric.key]}%`
                                  : d[metric.key]?.toLocaleString('pt-PT') || 0}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="h-[400px] flex items-center justify-center">
              <div className="text-center text-slate-500">
                <MapPin className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">Selecione distritos para comparar</h3>
                <p className="text-sm mt-2">Escolha entre 2 e 4 distritos na lista à esquerda</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
